// src/components/quickcreate/forms/QuickCreateSite.jsx
// Quick create a site inline — always under a client (client_id required)
//
// Props:
//   companyId   — string
//   clientId    — string (required — site always belongs to a client)
//   clientName  — string (for display)
//   onCreated   — fn({ id, site_name, address, client_id, company_id })
//   onCancel    — fn

import { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";

export default function QuickCreateSite({ companyId, clientId, clientName, onCreated, onCancel }) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [form, setForm] = useState({
    site_name: "",
    address:   "",
    lat:       null,
    lng:       null,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  // Guard — client must be selected before creating a site
  if (!clientId) {
    return (
      <div className="space-y-3">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <p className="text-xs text-amber-700 font-medium">
            ⚠️ Select or create a Client first before adding a Site.
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="w-full py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 font-medium hover:bg-slate-50 transition"
        >
          Close
        </button>
      </div>
    );
  }

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSave = async () => {
    if (!form.site_name.trim()) { setError("Site name is required"); return; }

    setSaving(true);
    try {
      const docRef = await addDoc(collection(db, "sites"), {
        company_id:        companyId,
        client_id:         clientId,
        client_name:       clientName || "",
        site_name:         form.site_name.trim(),
        address:           form.address.trim(),
        location_link:     form.lat && form.lng
          ? `https://www.google.com/maps?q=${form.lat},${form.lng}`
          : "",
        lat:               form.lat  || null,
        lng:               form.lng  || null,
        created_at:        serverTimestamp(),
        created_by:        user.uid || user.id || "",
        created_by_name:   user.name || "",
      });

      onCreated({
        id:          docRef.id,
        site_name:   form.site_name.trim(),
        address:     form.address.trim(),
        client_id:   clientId,
        company_id:  companyId,
      });

    } catch (err) {
      console.error("QuickCreateSite error:", err);
      setError(err.message || "Failed to create site");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Client context badge */}
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
        <span className="text-xs text-slate-400">Client:</span>
        <span className="text-xs font-semibold text-slate-700">{clientName || clientId}</span>
      </div>

      <p className="text-xs text-slate-400">
        Site will be created under the selected client.
      </p>

      {/* site_name */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
          Site Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          placeholder="e.g. AIIMS Block B"
          value={form.site_name}
          onChange={e => set("site_name", e.target.value)}
          autoFocus
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300"
        />
      </div>

      {/* address */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
          Address <span className="text-slate-300">(optional)</span>
        </label>
        <textarea
          rows={2}
          placeholder="e.g. AIIMS Campus, Ansari Nagar, New Delhi"
          value={form.address}
          onChange={e => set("address", e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm resize-none outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300"
        />
      </div>

      {/* GPS */}
      <div>
        <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
          GPS Location <span className="text-slate-300">(optional)</span>
        </label>
        <div className="flex gap-2 items-center">
          <input readOnly
            value={form.lat && form.lng ? `${form.lat.toFixed(5)}, ${form.lng.toFixed(5)}` : ""}
            placeholder="Capture GPS at site"
            className="flex-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-slate-50 text-slate-400"
          />
          <button type="button"
            onClick={() => {
              if (!navigator.geolocation) { setError("GPS not supported"); return; }
              navigator.geolocation.getCurrentPosition(
                pos => setForm(f => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude })),
                () => setError("Location permission denied")
              );
            }}
            className="px-3 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-semibold whitespace-nowrap hover:bg-teal-700">
            📍 GPS
          </button>
        </div>
        {form.lat && form.lng && (
          <p className="text-xs text-teal-600 mt-1 font-semibold">✅ GPS captured</p>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 font-medium hover:bg-slate-50 transition disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition disabled:opacity-40"
        >
          {saving ? "Saving..." : "Create Site"}
        </button>
      </div>
    </div>
  );
}
